import { Component, OnInit, AfterViewInit, HostListener, ElementRef, inject, DestroyRef, effect } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { ConfirmationDialogComponent } from './shared/components/confirmation-dialog.component';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { TopbarComponent } from './core/layout/topbar/topbar.component';
import { TrialBannerComponent } from './shared/components/trial-banner/trial-banner.component';
import { BanniereMiseAJourComponent } from './shared/components/banniere-mise-a-jour/banniere-mise-a-jour.component';
import { TrialWidgetComponent } from './shared/components/trial-widget/trial-widget.component';
import { ToastModule } from 'primeng/toast';
import { MenuService } from './core/services/menu.service';
import { User } from './core/models/user.model';
import { Warehouse } from './core/models/warehouse.model';
import { WarehouseService } from './core/services/warehouse.service';
import { PermissionService } from './features/security/services/permission.service';
import { AuthService } from './core/services/auth.service';
import { OfflineStateService } from './core/offline/services/offline-state.service';
import { HydrationService } from './core/offline/services/hydration.service';
import { SyncPushService } from './core/offline/services/sync-push.service';
import { PinVaultService } from './core/services/pin-vault.service';
import { VerrouPosteAdmin } from './core/services/verrou-poste';
import { appliquerInerte } from './core/services/verrou-inerte';
import { EcranVerrouComponent } from './shared/components/ecran-verrou/ecran-verrou.component';
import { environment } from '../environments/environment';
import { CompanyService } from './core/services/company.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, TranslateModule, ConfirmationDialogComponent, ConfirmDialogComponent, TopbarComponent, TrialBannerComponent, BanniereMiseAJourComponent, TrialWidgetComponent, ToastModule, EcranVerrouComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'StockFlow';
  isStandalonePage = false;
  currentUser: User | null = null;
  warehouses: Warehouse[] = [];
  private userLoaded = false;
  private readonly inertes = new Set<Element>();
  private readonly destroyRef = inject(DestroyRef);

  // FAB state
  fabOpen = false;
  fabEditMode = false;
  fabEditSearch = '';
  fabDragging = false;
  fabX = typeof window !== 'undefined' ? window.innerWidth - 90 : 0;
  fabY = typeof window !== 'undefined' ? window.innerHeight - 160 : 0;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private dragStartX = 0;
  private dragStartY = 0;
  private hasMoved = false;

  menuService: MenuService;
  readonly isTauri = !!(environment as Record<string, unknown>)['isTauri'];
  readonly isDevEnv = !!(environment as Record<string, unknown>)['showEnvironmentBanner'];
  readonly hydrationStatus;
  readonly hydrationProgress;

  constructor(
    private router: Router,
    private warehouseService: WarehouseService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private offlineState: OfflineStateService,
    private hydrationService: HydrationService,
    private syncPushService: SyncPushService,
    private pinVaultService: PinVaultService,
    readonly verrou: VerrouPosteAdmin,
    private companyService: CompanyService,
    menuService: MenuService
  ) {
    this.hydrationStatus = this.offlineState.hydrationStatus;
    this.hydrationProgress = this.offlineState.hydrationProgress;
    this.menuService = menuService;

    // Poste verrouille : tout le reste de la page devient inerte, dialogues PrimeNG compris.
    effect(() => appliquerInerte(this.verrou.verrouille(), this.elementRef.nativeElement, 'app-ecran-verrou', this.inertes));

    // Detect standalone pages from the initial URL before the first render
    const initialUrl = window.location.pathname;
    this.isStandalonePage = this.checkStandalonePage(initialUrl);

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      const url = this.router.url;
      const wasStandalone = this.isStandalonePage;
      this.isStandalonePage = this.checkStandalonePage(url);
      this.fabOpen = false;

      // Load user data when navigating from login/select-company to an authenticated page
      if (wasStandalone && !this.isStandalonePage) {
        this.loadUserData();
        this.verrou.demarrer();
      }
    });

    // Initialize translation
    translate.addLangs(['fr', 'en', 'es', 'ht']);
    translate.setDefaultLang('fr');

    const savedLang = localStorage.getItem('app-language') || localStorage.getItem('lang') || 'fr';
    translate.use(savedLang);

  }

  private checkStandalonePage(url: string): boolean {
    return url.includes('/login') || url.includes('/select-company') || url.includes('/choose-company') || url.includes('/register') || url.includes('/verify-email') || url.includes('/plans/public') || url.includes('/onboarding') || url.includes('/pin');
  }

  async ngOnInit() {
    if (!this.isStandalonePage) {
      // PIN check: hors ligne, seul le coffre PIN ouvre une session (PWA comme
      // Tauri). Sans coffre, ou sans PIN saisi, on redirige vers l'ecran PIN.
      const pinRedirect = await this.checkPinRequired();
      if (pinRedirect) {
        this.hideBootScreen();
        return;
      }

      this.verrou.demarrer();
      await this.loadUserData();
      if (this.isTauri) {
        await this.startOfflineHydration();
      }
    }
    this.hideBootScreen();
  }

  /** Check if PIN is required and redirect if so. Returns true if redirected. */
  private async checkPinRequired(): Promise<boolean> {
    const email = localStorage.getItem('currentUserEmail');
    if (!email) return false;

    const hasVault = await this.pinVaultService.hasVault(email);

    if (!navigator.onLine) {
      // Offline: must unlock with PIN
      if (hasVault) {
        this.router.navigate(['/pin'], { queryParams: { mode: 'unlock' } });
        return true;
      }
      // No vault + offline = blocked
      this.router.navigate(['/pin'], { queryParams: { mode: 'blocked' } });
      return true;
    }

    // Online + no vault: user will be prompted via PIN setup dialog after login
    // Do NOT redirect to /pin for setup — PIN creation happens from login flow
    return false;
  }

  private hideBootScreen(): void {
    const el = document.getElementById('sf-boot-screen');
    if (!el) return;
    el.classList.add('hiding');
    setTimeout(() => el.remove(), 500);
    document.getElementById('sf-boot-style')?.remove();
  }

  private updateBootMessage(msg: string, progress?: string): void {
    const msgEl = document.getElementById('sf-boot-msg');
    const progEl = document.getElementById('sf-boot-progress');
    if (msgEl) msgEl.textContent = msg;
    if (progEl) progEl.textContent = progress ?? '';
  }

  private async startOfflineHydration(): Promise<void> {
    const companyId = await this.authService.getCompanyId();
    if (!companyId) return;

    const numericCompanyId = Number(companyId);

    // Register auto-sync on reconnect
    this.offlineState.onReconnect(() => {
      this.syncPushService.pushAll().catch(() => {});
      this.runHydration(numericCompanyId);
    });

    // If offline, DB already has cached data from previous session — just mark as done
    if (!navigator.onLine) {
      this.offlineState.setHydrationStatus('done');
      return;
    }

    // Push pending offline transactions
    this.syncPushService.pushAll().catch(() => {});

    // Start hydration — boot screen stays visible during this
    this.updateBootMessage(this.translate.instant('OFFLINE.HYDRATION_TITLE') as string);
    await this.runHydration(numericCompanyId);
  }

  private async runHydration(companyId: number): Promise<void> {
    this.offlineState.setHydrationStatus('hydrating');
    // Subscribe to progress updates and push to boot screen
    const progressEffect = setInterval(() => {
      const p = this.hydrationProgress();
      if (p.total > 0) {
        const label = p.collection || '';
        this.updateBootMessage(
          this.translate.instant('OFFLINE.HYDRATION_MESSAGE') as string,
          `${p.current} / ${p.total}${label ? ' — ' + label : ''}`,
        );
      }
    }, 200);

    try {
      const needsFull = await this.hydrationService.needsFullHydration(companyId);
      if (needsFull) {
        await this.hydrationService.hydrateAll(companyId);
      } else {
        await this.hydrationService.hydrateDelta(companyId);
      }
      this.offlineState.setHydrationStatus('done');
    } catch {
      this.offlineState.setHydrationStatus('error');
    } finally {
      clearInterval(progressEffect);
    }
  }

  ngAfterViewInit() {
    // Position FAB in bottom-right after view is ready
    setTimeout(() => {
      this.fabX = window.innerWidth - 90;
      this.fabY = window.innerHeight - 160;
    });
  }

  private async loadUserData() {
    const isAuth = await this.authService.isAuthenticated();
    if (isAuth) {
      // Skip data loading if user has no company (onboarding in progress)
      const companyId = await this.authService.getCompanyId();
      if (!companyId) return;
      this.loadWarehouses();
      this.loadCurrentUser();
      // Le choix de l'entreprise pour le verrou (duree d'inactivite) vient avec ses donnees. Hors ligne : la valeur par defaut.
      this.companyService.getCompanyInfo().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ error: () => undefined });
    }
  }

  async loadCurrentUser() {
    if (this.userLoaded) return;
    const authUser = await this.authService.getCurrentUser();
    if (!authUser) {
      this.currentUser = null;
      return;
    }

    // Set initial data from Cognito
    this.currentUser = {
      id: 0,
      name: authUser.name || authUser.username,
      email: authUser.email,
      avatarUrl: authUser.avatarUrl,
      isActive: true,
      createdAt: new Date(),
      username: authUser.username
    };

    // Reuse DB user already resolved by PermissionService (avoids extra users API call)
    const dbUser = this.permissionService.resolvedDbUser();
    if (dbUser) {
      this.enrichCurrentUser(dbUser);
      this.userLoaded = true;
    } else {
      // PermissionService may still be loading — watch for the signal change
      const checkInterval = setInterval(() => {
        const resolved = this.permissionService.resolvedDbUser();
        if (resolved) {
          this.enrichCurrentUser(resolved);
          this.userLoaded = true;
          clearInterval(checkInterval);
        } else if (this.permissionService.initialized()) {
          // Permissions loaded but no DB user found — keep Cognito data
          this.userLoaded = true;
          clearInterval(checkInterval);
        }
      }, 100);
      // Safety timeout: stop waiting after 5s
      setTimeout(() => { clearInterval(checkInterval); this.userLoaded = true; }, 5000);
    }
  }

  private enrichCurrentUser(dbUser: any) {
    this.currentUser = {
      ...this.currentUser!,
      id: dbUser.id,
      name: dbUser.name || this.currentUser!.name,
      avatarUrl: dbUser.avatarUrl || this.currentUser!.avatarUrl,
      firstName: dbUser.firstName,
      lastName: dbUser.lastName,
      roleNames: dbUser.roleNames,
      roles: dbUser.roles
    };
  }

  loadWarehouses() {
    this.warehouseService.getAll().subscribe({
      next: data => { this.warehouses = data; },
      error: () => { /* silently ignore — user may not have warehouse access */ }
    });
  }

  onLanguageChange(lang: string) {
    this.translate.use(lang);
    localStorage.setItem('app-language', lang);
  }

  // FAB methods
  toggleFab(): void {
    if (!this.hasMoved) {
      this.fabOpen = !this.fabOpen;
    }
  }

  onFabMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.fabDragging = true;
    this.hasMoved = false;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragOffsetX = event.clientX - this.fabX;
    this.dragOffsetY = event.clientY - this.fabY;
  }

  onFabTouchStart(event: TouchEvent): void {
    const touch = event.touches[0];
    this.fabDragging = true;
    this.hasMoved = false;
    this.dragStartX = touch.clientX;
    this.dragStartY = touch.clientY;
    this.dragOffsetX = touch.clientX - this.fabX;
    this.dragOffsetY = touch.clientY - this.fabY;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.fabDragging) return;
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.hasMoved = true;
    this.fabX = Math.max(0, Math.min(window.innerWidth - 62, event.clientX - this.dragOffsetX));
    this.fabY = Math.max(0, Math.min(window.innerHeight - 62, event.clientY - this.dragOffsetY));
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.fabDragging) return;
    const touch = event.touches[0];
    const dx = touch.clientX - this.dragStartX;
    const dy = touch.clientY - this.dragStartY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.hasMoved = true;
    this.fabX = Math.max(0, Math.min(window.innerWidth - 62, touch.clientX - this.dragOffsetX));
    this.fabY = Math.max(0, Math.min(window.innerHeight - 62, touch.clientY - this.dragOffsetY));
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDragEnd(): void {
    this.fabDragging = false;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (this.fabOpen && !this.elementRef.nativeElement.querySelector('.fab-wrapper')?.contains(event.target)) {
      this.fabOpen = false;
    }
  }

  fabNavigate(route: string): void {
    this.fabOpen = false;
    this.fabEditMode = false;
    this.router.navigate([route]);
  }

  toggleFabEdit(): void {
    this.fabEditMode = !this.fabEditMode;
    if (!this.fabEditMode) this.fabEditSearch = '';
  }

  get filteredMenuSections() {
    const q = this.fabEditSearch.trim().toLowerCase();
    if (!q) return this.menuService.menuSections();
    return this.menuService.menuSections()
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          if (item.separator || !item.routerLink) return false;
          const translated = this.translate.instant(item.label) as string;
          return translated.toLowerCase().includes(q) || item.label.toLowerCase().includes(q);
        })
      }))
      .filter(section => section.items.length > 0);
  }

  toggleShortcut(route: string): void {
    this.menuService.toggleShortcut(route);
  }

  isShortcut(route: string): boolean {
    return this.menuService.isShortcut(route);
  }
}
